import React, { useEffect, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import LoadingButton from '../../components/Buttons/LoadingButton';
import { toast } from 'react-toastify';
import './SurveyCategoryQuestionManager.css';
import {
  fetchSurveys,
  createSurvey,
  fetchCategories,
  fetchAllQuestions,
  fetchQuestionsByCategory,
  updateQuestionCategory,
  linkCategoryToSurvey,
  unlinkCategoryFromSurvey,
} from '../../services/SurveyCategoryQuestion';

interface Survey {
  id: string;
  title: string;
}

interface Category {
  id: string;
  en_name: string;
  ar_name: string;
  surveyIds: string[];
}

interface Question {
  id: string;
  en_questionText: string;
  questionCategoryId: string | null;
}

const SurveyCategoryQuestionManager: React.FC = () => {
  const { t } = useTranslation();
  const tk = (key: string) => String(t(`surveyCategoryQuestion.${key}`));

  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [newSurveyTitle, setNewSurveyTitle] = useState('');

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const [categoryQuestions, setCategoryQuestions] = useState<Question[]>([]);
  const [originalCategoryQuestionIds, setOriginalCategoryQuestionIds] = useState<string[]>([]);
  const [unlinkedQuestions, setUnlinkedQuestions] = useState<Question[]>([]);

  useEffect(() => {
    fetchSurveys().then((data: { data?: Survey[] }) => setSurveys(data.data || []));
    fetchCategories().then(setCategories);
  }, []);

  const loadCategoryQuestions = async (categoryId: string) => {
    const catQs = await fetchQuestionsByCategory(categoryId);
    const allQs = await fetchAllQuestions();
    setCategoryQuestions(catQs);
    setOriginalCategoryQuestionIds(catQs.map((q: { id: string }) => q.id));
    setUnlinkedQuestions(allQs.filter((q: Question) => q.questionCategoryId !== categoryId));
  };

  const handleSurveySelect = (survey: Survey) => {
    setSelectedSurveyIds((prev) =>
      prev.includes(survey.id) ? prev.filter((id) => id !== survey.id) : [...prev, survey.id]
    );
  };

  const handleCreateSurvey = async () => {
    try {
      const data = await createSurvey(newSurveyTitle);
      setSurveys((prev) => [...prev, data]);
      setSelectedSurveyIds((prev) => [...prev, data.id]);
      setShowSurveyModal(false);
      setNewSurveyTitle('');
    } catch (e) {
      console.error(e);
      toast.error(tk('toast_error'));
    }
  };

  const handleRemoveQuestion = (id: string) => {
    setCategoryQuestions(categoryQuestions.filter((q) => q.id !== id));
  };

  const handleAddUnlinkedQuestion = (q: Question) => {
    setCategoryQuestions([...categoryQuestions, q]);
    setUnlinkedQuestions(unlinkedQuestions.filter((uq) => uq.id !== q.id));
  };

  const handleSubmit = async () => {
    if (selectedSurveyIds.length === 0 || selectedCategoryIds.length === 0) {
      toast.error(tk('toast_select'));
      return;
    }

    try {
      for (const categoryId of selectedCategoryIds) {
        const category = categories.find((c) => c.id === categoryId);
        const originalSurveyIds = category?.surveyIds || [];

        const toAdd = selectedSurveyIds.filter((id) => !originalSurveyIds.includes(id));
        const toRemove = originalSurveyIds.filter((id) => !selectedSurveyIds.includes(id));

        for (const id of toAdd) await linkCategoryToSurvey(categoryId, id);
        for (const id of toRemove) await unlinkCategoryFromSurvey(categoryId, id);

        if (selectedCategoryIds.length === 1) {
          const currentIds = categoryQuestions.map((q) => q.id);
          const added = currentIds.filter((id) => !originalCategoryQuestionIds.includes(id));
          const removed = originalCategoryQuestionIds.filter((id) => !currentIds.includes(id));

          for (const id of added) await updateQuestionCategory(id, categoryId);
          for (const id of removed) await updateQuestionCategory(id, null);
        }
      }

      fetchCategories().then(setCategories);
      toast.success(tk('toast_success'));
    } catch (e) {
      console.error(e);
      toast.error(tk('toast_error'));
    }
  };

  const surveyTitlesForCategory = (c: Category) =>
    (c.surveyIds || [])
      .map((id) => surveys.find((s) => s.id === id)?.title)
      .filter(Boolean)
      .join(', ');

  return (
    <div className="root scq-page">
      <header className="scq-header">
        <h1 className="scq-title">{tk('page_title')}</h1>
        <p className="scq-subtitle">{tk('page_subtitle')}</p>
      </header>

      <div className="scq-layout">
        <section className="scq-card scq-card--categories" aria-labelledby="scq-cat-heading">
          <div className="scq-card-head">
            <h2 id="scq-cat-heading" className="scq-card-title">
              {tk('category_label')}
            </h2>
          </div>
          <div className="scq-category-scroll">
            {categories.length === 0 ? (
              <p className="scq-empty">{tk('no_categories')}</p>
            ) : (
              categories.map((c) => {
                const isSelected = selectedCategoryIds.includes(c.id);
                const linkedTitles = surveyTitlesForCategory(c);

                const activate = async () => {
                  setSelectedCategoryIds([c.id]);
                  await loadCategoryQuestions(c.id);
                  setSelectedSurveyIds(c.surveyIds || []);
                };

                const handleClick = async () => {
                  if (isSelected) {
                    setSelectedCategoryIds([]);
                    setCategoryQuestions([]);
                    setUnlinkedQuestions([]);
                    setSelectedSurveyIds([]);
                  } else {
                    await activate();
                  }
                };

                const onKeyDown = (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void handleClick();
                  }
                };

                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    className={`scq-category-item${isSelected ? ' scq-category-item--selected' : ''}`}
                    onClick={() => void handleClick()}
                    onKeyDown={onKeyDown}
                  >
                    <div className="scq-category-item-main">
                      <span className="scq-category-name">{c.en_name}</span>
                      {linkedTitles ? (
                        <span className="scq-category-linked">
                          {tk('linked_to')}: {linkedTitles}
                        </span>
                      ) : null}
                    </div>
                    {isSelected && c.surveyIds && c.surveyIds.length > 0 && (
                      <div className="scq-category-badges">
                        <span className="scq-micro-label">{tk('selected_surveys')}</span>
                        <div className="scq-inline-chips">
                          {c.surveyIds.map((id) => {
                            const survey = surveys.find((s) => s.id === id);
                            return survey ? (
                              <span key={survey.id} className="scq-pill scq-pill--muted">
                                {survey.title}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <div className="scq-main-col">
          <section className="scq-card" aria-labelledby="scq-surveys-heading">
            <div className="scq-card-head scq-card-head--row">
              <div>
                <h2 id="scq-surveys-heading" className="scq-card-title">
                  {tk('surveys_label')}
                </h2>
                <p className="scq-card-hint">{tk('surveys_hint')}</p>
              </div>
              <button type="button" className="scq-btn scq-btn-primary" onClick={() => setShowSurveyModal(true)}>
                <FaPlus size={14} aria-hidden />
                {tk('new_survey')}
              </button>
            </div>
            <div className="scq-chip-grid" role="list">
              {surveys.map((s) => {
                const on = selectedSurveyIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="listitem"
                    className={`scq-chip${on ? ' scq-chip--on' : ''}`}
                    onClick={() => handleSurveySelect(s)}
                  >
                    <span className="scq-chip-text">{s.title}</span>
                    <span className="scq-chip-mark" aria-hidden>
                      {on ? '✓' : '+'}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {selectedCategoryIds.length === 1 && (
            <section className="scq-card scq-card--questions" aria-labelledby="scq-q-heading">
              <h2 id="scq-q-heading" className="scq-card-title scq-card-title--spaced">
                {tk('in_category')}
              </h2>
              <div className="scq-chip-grid scq-chip-grid--tall" role="list">
                {categoryQuestions.length === 0 ? (
                  <p className="scq-empty scq-empty--inline">{t('question.no_questions')}</p>
                ) : (
                  categoryQuestions.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      role="listitem"
                      className="scq-chip scq-chip--removable scq-chip--on"
                      onClick={() => handleRemoveQuestion(q.id)}
                      title={tk('remove_hint')}
                    >
                      <span className="scq-chip-text">{q.en_questionText}</span>
                      <span className="scq-chip-remove" aria-hidden>
                        ×
                      </span>
                    </button>
                  ))
                )}
              </div>

              <h3 className="scq-subheading">{tk('add_questions_label')}</h3>
              <p className="scq-card-hint scq-card-hint--tight">{tk('add_questions_hint')}</p>
              <div className="scq-chip-grid scq-chip-grid--tall" role="list">
                {unlinkedQuestions.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    role="listitem"
                    className="scq-chip scq-chip--add"
                    onClick={() => handleAddUnlinkedQuestion(q)}
                  >
                    <span className="scq-chip-text">{q.en_questionText}</span>
                    <span className="scq-chip-mark" aria-hidden>
                      +
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="scq-actions">
            <LoadingButton
              onClick={handleSubmit}
              variant="success"
              className="scq-submit-btn"
              loadingText={tk('submitting')}
            >
              {tk('submit')}
            </LoadingButton>
          </div>
        </div>
      </div>

      <Modal
        show={showSurveyModal}
        onHide={() => setShowSurveyModal(false)}
        className="scq-modal"
        contentClassName="scq-modal-content"
        backdropClassName="scq-modal-backdrop"
        centered
      >
        <Modal.Header closeButton className="scq-modal-header">
          <Modal.Title>{tk('modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="scq-modal-body">
          <Form.Label className="scq-field-label">{tk('modal_field_label')}</Form.Label>
          <Form.Control
            type="text"
            value={newSurveyTitle}
            onChange={(e) => setNewSurveyTitle(e.target.value)}
            placeholder={tk('modal_placeholder')}
            className="scq-input"
          />
        </Modal.Body>
        <Modal.Footer className="scq-modal-footer">
          <button type="button" className="scq-btn scq-btn-muted" onClick={() => setShowSurveyModal(false)}>
            {tk('modal_cancel')}
          </button>
          <LoadingButton
            onClick={handleCreateSurvey}
            variant="success"
            className="scq-btn scq-btn-primary scq-modal-save"
            disabled={!newSurveyTitle.trim()}
            loadingText={tk('modal_creating')}
          >
            {tk('modal_create')}
          </LoadingButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyCategoryQuestionManager;
